import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { TableWrapper, CardsContainer, EmptyStateContainer } from './styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import instance from '../../../axios-instance';
import { useUpdateEffect } from 'react-use';
import { DeleteModal, ErrorModal, TableSkeleton } from '@jadwalh/ui';
import { useSelector } from 'react-redux';
import { getPermissions } from '../../../store/profile.slice';
import { toast } from 'react-toastify';
import { getProfile } from '../../../api/profile.api';
import { WorkflowCard } from './components/WorkflowCard';

export default function ModelsTable() {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const permissions = useSelector(getPermissions);

  const [searchParams, setSearchParams] = useSearchParams();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(0);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  useUpdateEffect(() => {
    setSearchParams(searchParams);
  }, []);

  // Close menu on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (openMenuId !== null) {
        setOpenMenuId(null);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [openMenuId]);

  const {
    data,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    refetch: refetchWorkflows,
  } = useInfiniteQuery(
    ['workflows', searchParams.toString()],
    ({ pageParam = 1 }) =>
      instance.get(
        `/app/workflows?page=${pageParam}&limit=30&${searchParams.toString()}`
      ),
    {
      getNextPageParam: (lastPage) => {
        const nextPageLink = lastPage.data.links.next;
        return nextPageLink
          ? new URL(nextPageLink).searchParams.get('page')
          : undefined;
      },
      select: (response) => {
        return {
          pages: response.pages
            .map((page: any) => page.data.data)
            .flat() as any[],
          pageParams: response.pageParams,
        };
      },
      keepPreviousData: true,
    }
  );

  const handleDelete = (id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const { mutateAsync } = useMutation({
    mutationFn: (id: number) => {
      return instance.delete(`app/workflows/${id}`);
    },
    onSuccess: () => {
      refetchWorkflows();
      getProfile();
      toast.success(formatMessage({ id: 'deletedSuccessfully' }));
    },
  });

  const { mutateAsync: mutateDuplicate, isLoading: duplicateLoading } =
    useMutation(
      ['workflows'],
      (id: number) => instance.get(`/app/workflows/${id}/duplicate`),
      {
        onSuccess: () => refetchWorkflows(),
      }
    );

  const handleEdit = (id: number) => {
    navigate(`/workflows/update/${id}`);
  };

  // Infinite scroll handler for window scroll
  useEffect(() => {
    const handleWindowScroll = () => {
      const scrollPosition = window.innerHeight + window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;
      const threshold = 100;

      if (
        documentHeight - scrollPosition < threshold &&
        hasNextPage &&
        !isFetching
      ) {
        fetchNextPage();
      }
    };

    window.addEventListener('scroll', handleWindowScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleWindowScroll);
    };
  }, [hasNextPage, isFetching, fetchNextPage]);

  if (
    isLoading ||
    (Array.isArray(permissions) && !permissions.includes('workflows.index'))
  ) {
    return <TableSkeleton />;
  }

  return (
    Array.isArray(permissions) &&
    permissions.includes('workflows.index') && (
      <TableWrapper
        className={`${
          (!data?.pages || data?.pages.length === 0) && 'emptyTable'
        }`}
      >
        {!data?.pages || data?.pages.length === 0 ? (
          <EmptyStateContainer>
            <p className="empty-title">
              {formatMessage({ id: 'noWorkflows' })}
            </p>
            <p className="empty-description">
              {formatMessage({ id: 'noWorkflowsDescription' })}
            </p>
          </EmptyStateContainer>
        ) : (
          <CardsContainer>
            {data?.pages.map((record: any) => (
              <WorkflowCard
                key={record.id}
                record={record}
                permissions={Array.isArray(permissions) ? permissions : []}
                openMenuId={openMenuId}
                setOpenMenuId={setOpenMenuId}
                onEdit={handleEdit}
                onDuplicate={mutateDuplicate}
                onDelete={handleDelete}
              />
            ))}
          </CardsContainer>
        )}
        {showDeleteModal && (
          <DeleteModal
            showDeleteModal={showDeleteModal}
            setShowDeleteModal={setShowDeleteModal}
            message="deleteFormConfirm"
            submit={() => mutateAsync(deleteId)}
          />
        )}
        {showErrorModal && (
          <ErrorModal
            showErrorModal={showErrorModal}
            setShowErrorModal={setShowErrorModal}
            message={errorMessage}
            showCloseBtn={true}
          />
        )}
      </TableWrapper>
    )
  );
}
